import { useWorkspaceText } from './interface-copy';
import { useLibraryUploadWiring } from './library-feed/use-library-upload-wiring';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useWorkspace, useOptionalWorkspace } from './context';
import { ToastRegion } from '@/components/ui/toast';
import { WorkspaceIcon, type WorkspaceIconName } from './icons';
import {
  isSettingsView,
  settingsNavigation,
  viewDefinitions,
  workspaceNavigation,
} from './navigation';
import type { WorkspaceView } from './types';
import type { ViewDefinition } from './view-definitions';
import { SearchNavItem, WorkspaceFinder } from './finder';
import { WorkspacePages } from './pages';
import { WorkspaceCanvas } from './canvas';
import { CanvasLeaveDialog } from './canvas-leave-dialog';
import { useCanvasReturnFocus } from './canvas-focus';
import type { ActionRestage } from '@/components/assistant-ui/action-confirmation';
import { MeetingRecordingStrip } from './meeting-recording-strip';
import { NewProjectSheetHost } from './projects/new-project-sheet';
import { ChatPlaceCrumb } from './projects/place';
import {
  ProjectHomeActions,
  ProjectHomeCrumbs,
  ProjectHomeWelcome,
  projectHomeId,
} from './projects/home';
import { ProjectsNavSection } from './projects/nav';
import { ProjectAboutPanel } from './projects/about';
import { RecentChats, ChatNavigationFeedback } from './chat-navigation';
import { AboutChatButton } from './provenance-panel';
import { ChatGone, ChatHeaderActions, ChatScopeChip, ForkedFromBanner } from './chat-collaboration';
import { InboxNavItem } from './inbox';
import { isAppPageMode } from './apps/app-page-mode';
import { isNewAppCanvas } from './apps/new-app';
import { isNewAgentCanvas, makingNewAgent } from './agents/new-agent';
import { useNamespaceCopy } from '@/i18n/ns';
import appsNavCopy from '@/i18n/ns/apps-nav';
import workspaceShellCopy from '@/i18n/ns/workspace-shell';
import companySettingsCopy from '@/i18n/ns/company-settings';
import { useAppCopy } from '@/i18n/context';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import searchCopy from '@/i18n/ns/search';
import meetingsRecordCopy from '@/i18n/ns/meetings-record';
import knowledgePagesCopy from '@/i18n/ns/knowledge-pages';
import storefrontCopy from '@/i18n/ns/storefront';
import entrySurfaceCopy from '@/i18n/ns/entry-surfaces';
import modelPickerCopy from '@/i18n/ns/model-picker';
import billingCopy from '@/i18n/ns/billing';
import usageCopy from '@/i18n/ns/usage';
import voiceCopy from '@/i18n/ns/voice';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import workspaceTopbarCopy from '@/i18n/ns/workspace-topbar';
import { useMediaQuery } from '@/lib/mobile';
import { ResponsiveChatActions } from '@/mobile/chat-actions';
import { useMobileDrawer } from '@/mobile/use-drawer';
import { useTopbarCopy } from './topbar-copy';
import { useAgentFeatures, type AgentFeatures } from './agent-features';
import { useCompany } from '@/company/context';
import { StorefrontDesignWelcome } from './storefront/design-welcome';

/** A section behind a server flag shows only once the flags are read and it is on. */
function sectionShown(view: WorkspaceView, features: AgentFeatures | null) {
  const feature = (viewDefinitions[view] as ViewDefinition).feature;
  return !feature || features?.[feature] === true;
}

export function WorkspaceNavigation({ onLogout }: { onLogout?: () => void }) {
  const { text: uiText } = useWorkspaceText();
  const ws = useWorkspace();
  // The flags are read once the company is there, so a pending invitation is accepted first.
  const features = useAgentFeatures(useCompany().ready);
  const settings = isSettingsView(ws.view);
  const copy = useAppCopy();
  const shellCopy = useNamespaceCopy(workspaceShellCopy);
  const companyCopy = useNamespaceCopy(companySettingsCopy);
  const panels = useNamespaceCopy(artifactPanelsCopy);
  const search = useNamespaceCopy(searchCopy);
  const meetings = useNamespaceCopy(meetingsRecordCopy);
  const knowledge = useNamespaceCopy(knowledgePagesCopy);
  const storefront = useNamespaceCopy(storefrontCopy);
  const topbarCopy = useNamespaceCopy(workspaceTopbarCopy);
  const workspaceCopy = useNamespaceCopy(workspaceFeaturesCopy);
  const entry = useNamespaceCopy(entrySurfaceCopy);
  const models = useNamespaceCopy(modelPickerCopy);
  const billing = useNamespaceCopy(billingCopy);
  const usage = useNamespaceCopy(usageCopy);
  const voice = useNamespaceCopy(voiceCopy);
  const labels: Record<WorkspaceView, string> = {
    chat: search.newChat,
    inbox: topbarCopy.inbox,
    projects: workspaceCopy.projects,
    files: panels.libraryTitle,
    results: panels.libraryTitle,
    apps: panels.libraryKindApps,
    storefront: storefront.pageTitle,
    meetings: meetings.pageTitle,
    skills: search.groupSkills,
    agents: topbarCopy.agents,
    knowledge: knowledge.pageTitle,
    connections: topbarCopy.connections,
    account: copy.account,
    preferences: shellCopy.preferences,
    voice: voice.voiceMode,
    'personal-memory': companyCopy.personalMemory,
    desktop: entry.downloads,
    profile: companyCopy.companyProfile,
    processing: companyCopy.processing,
    models: models.settingsTitle,
    team: companyCopy.team,
    billing: billing.billingTitle,
    usage: usage.title,
  };
  const nav = (view: WorkspaceView, label?: string, icon?: WorkspaceIconName) => {
    const definition = viewDefinitions[view] as ViewDefinition;
    const navLabel = label ?? labels[view];
    const navIcon = icon ?? definition.icon;
    return (
      <button
        key={view}
        className={`ws-nav-item ${ws.view === view || (view === 'results' && ws.view === 'files') ? 'active' : ''}`}
        aria-current={ws.view === view ? 'page' : undefined}
        title={navLabel}
        onClick={() => ws.navigate(view)}
      >
        <WorkspaceIcon name={navIcon} />
        <span>{navLabel}</span>
        {!label && definition.tag && <small className="ws-nav-tag">{uiText('Early')}</small>}
      </button>
    );
  };
  return (
    <>
      <button
        className="mobile-drawer-overlay"
        aria-label={shellCopy.dismissNavigation}
        tabIndex={-1}
        hidden
      />
      <aside
        className={`ws-sidebar mobile-drawer ${ws.mobileOpen ? 'mobile-open' : ''}`}
        aria-label={settings ? shellCopy.settingsNavigation : shellCopy.workspaceNavigation}
      >
        <div className="ws-brand-row">
          <button className="ws-brand" aria-label={shellCopy.ponevaHome} onClick={ws.newChat}>
            <img src="/favicon.svg" alt="" />
            <span>poneva</span>
          </button>
          <button
            className="ws-icon-button ws-collapse"
            onClick={() => ws.setCompact(!ws.compact)}
            aria-label={ws.compact ? shellCopy.expandNavigation : shellCopy.collapseNavigation}
            title={ws.compact ? shellCopy.expandNavigation : shellCopy.collapseNavigation}
          >
            <WorkspaceIcon name="menu" size={16} />
          </button>
          <button
            className="ws-icon-button ws-mobile-only"
            onClick={() => ws.setMobileOpen(false)}
            aria-label={shellCopy.closeNavigation}
          >
            <WorkspaceIcon name="close" />
          </button>
        </div>
        {settings ? (
          <button
            className="ws-settings-back"
            onClick={ws.returnToWork}
            title={shellCopy.goBack}
            aria-label={shellCopy.goBack}
          >
            <WorkspaceIcon name="arrow" className="ws-back-arrow" size={16} />
            <span>{shellCopy.goBack}</span>
          </button>
        ) : (
          <>
            <button
              className="ws-company-switch"
              onClick={() => ws.navigate('profile')}
              title={shellCopy.companySettings}
            >
              <span className="ws-company-avatar">{ws.companyName.slice(0, 1)}</span>
              <span>
                <strong>{ws.companyName}</strong>
                <small>{shellCopy.workspace}</small>
              </span>
            </button>
            <div className="ws-chat-actions">
              <button
                className="ws-new-chat"
                onClick={ws.newChat}
                title={search.newChat}
                data-testid="sidebar-new-chat-button"
              >
                <WorkspaceIcon name="plus" />
                <span>{search.newChat}</span>
              </button>
              <RecentChats />
            </div>
          </>
        )}
        <nav aria-label={settings ? shellCopy.settingsSections : shellCopy.workspaceSections}>
          {settings ? (
            <>
              <div className="ws-nav-group">
                <p>{shellCopy.personal}</p>
                {settingsNavigation.personal.map((view) => nav(view))}
              </div>
              <div className="ws-nav-group">
                <p>{ws.companyName}</p>
                {settingsNavigation.company.map((view) => nav(view))}
              </div>
            </>
          ) : (
            <>
              <SearchNavItem />
              <InboxNavItem />
              {workspaceNavigation
                .filter((view) => sectionShown(view, features))
                .map((view) =>
                  view === 'projects' ? <ProjectsNavSection key={view} /> : nav(view),
                )}
            </>
          )}
        </nav>
        <div className="ws-sidebar-bottom">
          {!settings && nav('account', copy.settings, 'settings')}
          <button
            className="ws-admin"
            onClick={() => ws.navigate('account')}
            title={ws.user?.email ?? shellCopy.yourAccount}
          >
            <span className="ws-user-avatar">
              {(ws.user?.displayName ?? ws.user?.email ?? ws.companyName).slice(0, 1).toUpperCase()}
            </span>
            <span>
              <strong>{ws.user?.displayName ?? ws.user?.email ?? shellCopy.yourAccount}</strong>
              <small>{ws.companyName}</small>
            </span>
          </button>
          {onLogout && (
            <button className="ws-nav-item ws-logout" onClick={onLogout} title={copy.logout}>
              <WorkspaceIcon name="logout" size={16} />
              <span>{copy.logout}</span>
            </button>
          )}
        </div>
      </aside>
    </>
  );
}

/** The native chat remains mounted. Its stream, scroll and draft outlive page changes. */
export function WorkspaceShell({
  children,
  settings,
  onLogout,
}: {
  children: ReactNode;
  settings?: ReactNode;
  onLogout?: () => void;
}) {
  const ws = useOptionalWorkspace();
  return ws ? (
    <WorkspaceShellContent settings={settings} onLogout={onLogout}>
      {children}
    </WorkspaceShellContent>
  ) : (
    <>{children}</>
  );
}
function WorkspaceShellContent({
  children,
  settings,
  onLogout,
}: {
  children: ReactNode;
  settings?: ReactNode;
  onLogout?: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  useLibraryUploadWiring();
  const isSettings = isSettingsView(ws.view);
  // Where the canvas covers the chat (up to 1100px, workspace.css), a new App's or agent's waiting panel stays
  // closed so the composer is free; the App opens there with its first preview, the agent with its draft.
  const canvasCoversChat = useMediaQuery('(max-width: 1100px)');
  const waiting = isNewAppCanvas(ws.canvas) || isNewAgentCanvas(ws.canvas);
  // About this project sits beside the project's home under the top bar, not as a canvas.
  const aboutProject =
    !isSettings && ws.view === 'chat' && ws.canvas?.kind === 'project' ? ws.canvas.id : null;
  const canvas =
    isSettings || (canvasCoversChat && waiting) || ws.canvas?.kind === 'project' ? null : ws.canvas;
  const shell = useRef<HTMLDivElement>(null);
  useCanvasReturnFocus(Boolean(canvas), ws.canvasAutoOpened, shell);
  useMobileDrawer(shell, {
    open: ws.mobileOpen,
    blocked: Boolean(canvas) || ws.finderOpen,
    chat: ws.view === 'chat',
    onChange: ws.setMobileOpen,
  });
  const pageScroll = useRef<HTMLDivElement>(null);
  const scrollPositions = useRef(new Map<WorkspaceView, number>());
  const previousView = useRef(ws.view);
  // The last restage the server returned, kept across the canvas remount that
  // swaps to the replacing request (presentation only; it grants nothing).
  const [restage, setRestage] = useState<ActionRestage | null>(null);
  useEffect(() => {
    previousView.current = ws.view;
    if (pageScroll.current)
      pageScroll.current.scrollTop = scrollPositions.current.get(ws.view) ?? 0;
  }, [ws.view]);
  // A saved App on the Apps view is its own page: nothing of the workspace around it (DES1 spec 5).
  const appPage = isAppPageMode(ws.view, canvas);
  const fullCanvas = Boolean(canvas?.full) || appPage;
  const chatReady = ws.view === 'chat' && ws.chatStatus === 'ready' && !fullCanvas;
  const topbar = useTopbarCopy();
  // A project's home is the chat home in the project: its top bar names the project.
  const projectHome = ws.view === 'chat' && !fullCanvas && projectHomeId(ws) !== null;
  // About this project belongs to the home: once the chat there is sent, it closes.
  const aboutLeftHome = aboutProject !== null && !projectHome;
  useEffect(() => {
    if (aboutLeftHome) ws.openCanvas(null, { replace: true });
  }, [aboutLeftHome]);
  const title =
    ws.view === 'chat'
      ? ws.chatStatus === 'loading'
        ? topbar.openingChat
        : ws.chatStatus === 'error'
          ? topbar.chatUnavailable
          : (ws.threads.find((t) => t.id === ws.threadId)?.title ??
            ws.threadMeta?.title ??
            topbar.newChat)
      : topbar.place(ws.view);
  return (
    <div
      ref={shell}
      className={`ws-shell mobile-viewport-root ${ws.compact ? 'ws-compact' : ''} ${canvas ? 'ws-has-canvas' : ''} ${fullCanvas ? 'ws-canvas-full' : ''} ${appPage ? 'ws-app-page' : ''} ${isSettings ? 'ws-settings-open' : ''} ${ws.view === 'results' || ws.view === 'files' ? 'ws-library-open' : ''}`}
    >
      <a
        className="ws-skip-link"
        href="#ws-main-content"
        onClick={(event) => {
          event.preventDefault();
          const shell = event.currentTarget.closest('.ws-shell');
          const canvasElement = shell?.querySelector('.ws-canvas');
          // Responsive CSS can overlay the work area without changing the canvas route.
          const focusCanvas =
            fullCanvas || (canvasElement && getComputedStyle(canvasElement).position === 'fixed');
          const target = focusCanvas
            ? '.ws-canvas-top'
            : ws.view === 'chat'
              ? '.ws-chat-slot'
              : '.ws-page-scroll';
          shell?.querySelector<HTMLElement>(target)?.focus();
        }}
      >
        {topbar.skipToContent}
      </a>
      <WorkspaceNavigation onLogout={onLogout} />
      <NewProjectSheetHost />
      <div className={`ws-main-column ${aboutProject && projectHome ? 'ws-about-open' : ''}`}>
        <header className="ws-topbar">
          <div className="ws-breadcrumb">
            <button
              className="ws-icon-button ws-mobile-only"
              onClick={() => ws.setMobileOpen(!ws.mobileOpen)}
              aria-label={topbar.toggleNavigation}
              data-testid="mobile-menu-button"
            >
              <WorkspaceIcon name="menu" />
            </button>
            {projectHome ? (
              <ProjectHomeCrumbs />
            ) : (
              <>
                {ws.view === 'chat' ? <ChatPlaceCrumb /> : <span>{ws.companyName}</span>}
                <span className="ws-slash">/</span>
                <strong>{title}</strong>
              </>
            )}
            {chatReady && !ws.threadGone && <ChatScopeChip />}
          </div>
          {/* A page's own top-bar control on a phone (the Library's Owner menu), portalled in by that page. */}
          <div className="ws-topbar-slot" />
          {projectHome && chatReady && <ProjectHomeActions />}
          {chatReady && !ws.threadGone && !projectHome && (
            <ResponsiveChatActions key={ws.threadId}>
              <AboutChatButton />
              <ChatHeaderActions />
            </ResponsiveChatActions>
          )}
          {fullCanvas && !appPage && canvas && (
            <button
              className="ws-text-button"
              onClick={() => ws.openCanvas({ ...canvas, full: false })}
            >
              {uiRich('Return to conversation {value1}', {
                value1: <WorkspaceIcon name="chat" size={14} />,
              })}
            </button>
          )}
        </header>
        <MeetingRecordingStrip />
        {chatReady && !ws.threadGone && <ForkedFromBanner />}
        <div
          className="ws-chat-slot"
          role="region"
          aria-label={uiText('Conversation')}
          tabIndex={-1}
          hidden={ws.view !== 'chat' || fullCanvas || ws.chatStatus !== 'ready'}
          inert={ws.view !== 'chat' || fullCanvas || ws.chatStatus !== 'ready'}
        >
          {/* A reader's open chat was made private: a calm page, not an error. */}
          {ws.threadGone ? <ChatGone /> : children}
        </div>
        {ws.view === 'chat' && !fullCanvas && ws.chatStatus !== 'ready' && (
          <ChatNavigationFeedback />
        )}
        {aboutProject && projectHome && <ProjectAboutPanel key={aboutProject} id={aboutProject} />}
        <div
          className="ws-page-scroll"
          id="ws-main-content"
          role="region"
          aria-label={topbar.place(ws.view)}
          tabIndex={-1}
          ref={pageScroll}
          onScroll={(e) =>
            scrollPositions.current.set(previousView.current, e.currentTarget.scrollTop)
          }
          hidden={ws.view === 'chat' || fullCanvas}
        >
          {isSettings ? settings : <WorkspacePages />}
        </div>
        {/* Toasts centre on the work, not the viewport (DESIGN-SPEC §6.12). */}
        <div className="pc-toast-anchor">{!fullCanvas && <ToastRegion queue={ws.toasts} />}</div>
      </div>
      {canvas && (
        <WorkspaceCanvas
          // A chat's App canvas stays mounted as its preview changes: the App updates in place.
          key={canvas.kind === 'app-preview' ? canvas.kind : `${canvas.kind}:${canvas.id ?? ''}`}
          restage={restage}
          onRestaged={setRestage}
        />
      )}
      {/* The docked canvas asks with its own copy of the leave dialog; with
          none docked, the shell asks for the page (Knowledge's editor) and for
          its own exits: the sidebar, New chat, Recent chats and Back. */}
      {!canvas && (
        <CanvasLeaveDialog
          open={ws.canvasLeavePrompt}
          onCancel={ws.cancelCanvasLeave}
          onDiscard={ws.discardCanvasLeave}
        />
      )}
      <WorkspaceFinder />
      {fullCanvas && <ToastRegion queue={ws.toasts} placement="canvas" />}
    </div>
  );
}

export function WorkspaceWelcome() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const apps = useNamespaceCopy(appsNavCopy);
  const skill = ws.turnContext.intent.mode === 'skill-author';
  const app = !skill && isNewAppCanvas(ws.canvas);
  const agent = !skill && !app && makingNewAgent(ws.turnContext);
  const newProject = !skill && !app && !agent && ws.turnContext.newProject === true;
  if (!skill && ws.canvas?.kind === 'storefront') return <StorefrontDesignWelcome />;
  // A new chat in a project is the project's home: its name and brief above the composer.
  if (!skill && !app && !agent && !newProject && projectHomeId(ws)) return <ProjectHomeWelcome />;
  return (
    <div className="ws-welcome">
      <div className="ws-company-context">
        <span className="ws-company-context-label">
          {uiRich('{value1} Working with {value2}', {
            value1: <span className="ws-status-dot" />,
            value2: ws.companyName,
          })}
        </span>
        <button onClick={() => ws.navigate('knowledge')}>
          {uiRich('View context {value1}', { value1: <WorkspaceIcon name="arrow" size={14} /> })}
        </button>
      </div>
      <h1>
        {skill
          ? uiText('A way of working, made yours.')
          : app
            ? apps.appsNewHeading
            : agent
              ? uiText('Work that runs itself.')
              : newProject
                ? uiText('One place for one piece of work.')
                : uiText('Let’s move your work forward{value1}.', {
                    value1: ws.user?.displayName ? `, ${ws.user.displayName.split(' ')[0]}` : '',
                  })}
      </h1>
      <p>
        {skill
          ? uiText('Describe the work. Your Skill takes shape beside the conversation.')
          : app
            ? apps.appsNewIntro
            : agent
              ? uiText(
                  'Describe what it should do and when. Your agent takes shape beside the conversation. The draft does not run automatically.',
                )
              : newProject
                ? uiText(
                    'Say what the work is. Poneva drafts the project beside the conversation and brings in what you have already done. Nothing is created until you say so.',
                  )
                : uiText('Start with an idea below. Poneva will take it from there.')}
      </p>
    </div>
  );
}
